import { Context, createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

export interface PendingChangesContextType<T = any> {
	pendingChanges: boolean;
	isItemChanged: (item: T) => boolean;
	setItemChanged: (item: T, changed: boolean) => void;
	clearAllChanges: () => void;
}

const defaultContextValues = {
	pendingChanges: false,
	isItemChanged: undefined,
	setItemChanged: undefined,
	clearAllChanges: undefined,
};

function createPendingChangesContext<T = any>(contextValues?: PendingChangesContextType<T>) {
	return createContext<PendingChangesContextType<T>>(contextValues ?? defaultContextValues);
}
export const PendingChangesContext = createPendingChangesContext();
export function getPendingChangesContext<T = any>() {
	return PendingChangesContext as Context<PendingChangesContextType<T>>;
}
export const usePendingChangesContext = () => useContext(PendingChangesContext);
export function useUpdatePendingChanges(type: unknown) {
	const { setItemChanged } = usePendingChangesContext();
	return useCallback((changed: boolean) => setItemChanged?.(type, changed), [setItemChanged, type]);
}

interface PendingChangesProviderProps {
	children: ReactNode;
}

export function PendingChangesProvider(props: PendingChangesProviderProps) {
	const { children } = props;

	const [itemsChanged, setItemsChanged] = useState(() => new Set<unknown>());

	const isItemChanged = useCallback((item: unknown) => itemsChanged.has(item), [itemsChanged]);
	const setItemChanged = useCallback((item: unknown, changed: boolean) => {
		setItemsChanged((prev) => {
			if (prev.has(item) === changed) return prev;
			const items = new Set(prev);
			if (changed) items.add(item);
			else items.delete(item);
			return items;
		});
	}, []);

	const clearAllChanges = useCallback(() => setItemsChanged((prev) => (prev.size ? new Set() : prev)), []);

	const pendingChanges = itemsChanged.size > 0;

	const value = useMemo(
		() => ({ pendingChanges, isItemChanged, setItemChanged, clearAllChanges }),
		[pendingChanges, isItemChanged, setItemChanged, clearAllChanges],
	);

	return <PendingChangesContext.Provider value={value}>{children}</PendingChangesContext.Provider>;
}
